import { AddToCalendarButton } from "@web/emailCalendar/AddToCalendarButton";
import { AddTaskButton } from "@web/tasks/AddTaskButton";
import { ReplyThoughts } from "./ReplyThoughts";
import { SaveMailButton } from "@web/library/SaveMailButton";
// One decision card: eyebrow, serif title, why, blocks, history, and a footer that is either the
// "Next thing" actions, the armed ApproveStrip, the snooze picker, or the state of its work.
// Anatomy adapted from EveryInc/tend src/feed/CardView.tsx (MIT, Copyright (c) 2026 Every, Inc.).
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BookOpen, Info, Timer } from "lucide-react";
import { toast } from "sonner";
import type { CardSummary, CardView, ReceiptView } from "@shared/api";
import type { ActionKind } from "@shared/core";
import { cn } from "@web/lib/cn";
import { formatShortDateTime } from "@web/lib/time";
import { useNow } from "@web/lib/useNow";
import { CardSenderLinks, OpenInGmail } from "@web/notify/SenderLinks";
import { AccountChip } from "@web/ui/AccountChip";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { QueuedNoteEditor, WorkControls } from "@web/work/WorkControls";
import { CardMemory } from "@web/remember/CardMemory";
import { QueuedRunNote, ScheduledReturnNote } from "@web/sendLater/CardSendLater";
import { isExternal, keyedActions, nextThingLabel } from "../actions";
import { restoreCard } from "../cardOps";
import { checkWords, receiptWords } from "../receipts";
import { ReplyBadge } from "../ReplyBadge";
import { isToReview } from "../selectors";
import {
  doneLabel,
  URGENCY_LABEL,
  waitingLabel,
  workNeedsDecision,
  workPhaseLabel,
} from "../wording";
import { ApproveStrip } from "./ApproveStrip";
import { BlockView } from "./Blocks";
import { CardHistory } from "./CardHistory";
import { DraftNotice } from "./DraftNotice";
import { blocksBesideWhy } from "./face";
import { FormattedText } from "./FormattedText";
import { EmailLinksContext, emailLinkSet } from "./links";
import { mailCheckNotes } from "./mailCheck";
import { SmallerStepOffer, snoozeAfterOffer } from "./SmallerStepOffer";
import { SnoozePicker } from "./SnoozePicker";
import { CardStartLines } from "./StartLines";
import { RecommendedKey } from "./RecommendedKey";
import type { CardController } from "./useCardController";

export function CardMeta({
  card,
  showAccount,
  colorIndex,
  accountLabel,
  now,
}: {
  card: CardSummary;
  showAccount: boolean;
  colorIndex: number;
  accountLabel: string | undefined;
  now: number;
}) {
  const waiting = isToReview(card) ? waitingLabel(card.waitingSinceAt, now) : null;
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      <span className="mm-kicker truncate">{card.eyebrow}</span>
      {showAccount && card.accountEmail ? (
        <AccountChip email={card.accountEmail} label={accountLabel} colorIndex={colorIndex} />
      ) : null}
      {isToReview(card) && (card.urgency === "now" || card.urgency === "today") ? (
        <span className="rounded-full border border-line px-2 text-[11px] leading-5 text-ink-2">
          {URGENCY_LABEL[card.urgency]}
        </span>
      ) : null}
      {waiting ? <span className="text-xs text-muted">{waiting}</span> : null}
      <ReplyBadge threadKey={card.threadKey} />
    </div>
  );
}

function Receipts({ receipts }: { receipts: readonly ReceiptView[] }) {
  if (!receipts.length) return null;
  return (
    <section className="flex flex-col gap-2">
      <h3 className="mm-kicker">Receipts</h3>
      {receipts.map((r) => {
        const words = receiptWords(r);
        return (
          <div
            key={r.id}
            data-testid={`receipt-${r.kind}`}
            className="rounded-lg border border-calm-line bg-calm-soft px-3 py-2 text-sm"
          >
            <p className="font-medium text-ink">
              {words.title} · {r.allPassed ? "every check passed" : "some checks need a look"}
              <span className="font-normal text-muted"> · {formatShortDateTime(r.at)}</span>
            </p>
            {words.lines.length ? (
              <ul className="mt-1 flex flex-col gap-0.5 text-ink-2">
                {words.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : null}
            {words.links.length ? (
              <p className="mt-1 flex flex-wrap gap-x-3">
                {words.links.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-text underline underline-offset-2"
                  >
                    {l.label}
                  </a>
                ))}
              </p>
            ) : null}
            <ul className="mt-1 grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
              {Object.entries(r.checks).map(([name, value]) => (
                <li key={name} className="flex gap-1.5 text-ink-2">
                  <span aria-hidden className={value === false ? "text-note-ink" : "text-calm"}>
                    {value === false ? "○" : "✓"}
                  </span>
                  <span className="min-w-0 break-words">
                    {checkWords(name)}
                    {typeof value === "string" ? `: ${value}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </section>
  );
}

/** What MeeMail noticed about the email (unverified sender, hidden text), in calm words. */
function MailCheckNotes({ card }: { card: CardView }) {
  const notes = mailCheckNotes(card.mailCheck);
  if (!notes.length) return null;
  return (
    <ul data-testid="mail-check" className="flex flex-col gap-1 text-sm text-ink-2">
      {notes.map((note) => (
        <li key={note} className="flex items-start gap-1.5">
          <Info aria-hidden className="mt-0.5 size-4 shrink-0 text-muted" />
          <span>{note}</span>
        </li>
      ))}
    </ul>
  );
}

function FooterShell({
  children,
  tone = "plain",
}: {
  children: React.ReactNode;
  tone?: "plain" | "note";
}) {
  return (
    <footer
      className={cn(
        "flex flex-col gap-3 rounded-b-xl border-t px-5 py-3.5 md:px-7",
        tone === "note" ? "border-note-line bg-note/70" : "border-line bg-bg/60",
      )}
    >
      {children}
    </footer>
  );
}

function ReviewFooter({
  card,
  controller,
  defaultCleanupKind,
}: {
  card: CardView;
  controller: CardController;
  defaultCleanupKind: ActionKind | null;
}) {
  const armed = controller.armedAction;
  if (armed) {
    return (
      <FooterShell>
        <ApproveStrip
          card={card}
          action={armed}
          defaultCleanupKind={defaultCleanupKind}
          busy={controller.busy}
          onConfirm={controller.confirm}
          onCancel={controller.disarm}
          remind={controller.remindChoice}
          onRemind={controller.setRemindChoice}
          onRemindDays={controller.setRemindDays}
        />
      </FooterShell>
    );
  }
  if (controller.snoozeOpen) {
    return (
      <FooterShell>
        {/* U5: snoozed once before, the second snooze first offers a smaller step. */}
        {card.smallerStep ? <SmallerStepOffer card={card} controller={controller} /> : null}
        <SnoozePicker
          onPick={(until, label) => void snoozeAfterOffer(card, controller, until, label)}
          onClose={() => controller.setSnoozeOpen(false)}
          {...(card.smallerStep ? { title: "Or bring it back later…" } : {})}
        />
      </FooterShell>
    );
  }
  const keyed = keyedActions(card.actions);
  const configured = keyed.filter((k) => !k.injected);
  const local = keyed.filter((k) => k.injected);
  return (
    <FooterShell>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <p className="mm-kicker">Next thing</p>
          <p className="font-medium text-ink">
            {nextThingLabel(card.nextThing, card.actions)}
            <RecommendedKey actions={card.actions} />
          </p>
          {card.updatePending ? (
            <p className="text-sm text-note-ink">
              Updated · the new version shows in the next round
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {configured.map(({ action, key }) => (
            <Button
              key={action.id}
              size="sm"
              variant={action.variant === "primary" ? "primary" : "secondary"}
              disabled={controller.busy || (card.updatePending && isExternal(action))}
              aria-keyshortcuts={key ?? undefined}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => controller.press(action)}
            >
              {action.label}
              {key ? (
                <Kbd
                  className={
                    action.variant === "primary"
                      ? "border-current bg-transparent text-current opacity-80"
                      : undefined
                  }
                >
                  {key}
                </Kbd>
              ) : null}
            </Button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 border-t border-line pt-3">
        {local.map(({ action, key }) => (
          <Button
            key={action.id}
            size="sm"
            variant="ghost"
            disabled={controller.busy}
            aria-keyshortcuts={key ?? undefined}
            onPointerDown={(e) => e.preventDefault()}
            onClick={() => controller.press(action)}
          >
            {action.label}
            {key ? <Kbd>{key.toUpperCase()}</Kbd> : null}
          </Button>
        ))}
        <Button
          size="sm"
          variant="ghost"
          aria-keyshortcuts="m"
          onPointerDown={(e) => e.preventDefault()}
          onClick={controller.makeTime}
        >
          <Timer aria-hidden className="size-4" />
          Make time
          <Kbd>M</Kbd>
        </Button>
      </div>
    </FooterShell>
  );
}

function StateFooter({ card }: { card: CardView }) {
  const queryClient = useQueryClient();
  const restore = () =>
    restoreCard(queryClient, card.id).then(
      () => toast("Back in this round."),
      () => toast("Couldn't move it back just now."),
    );
  if (card.status === "done") {
    return (
      <FooterShell>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="mm-kicker">Done</p>
            <p className="font-medium text-ink">
              {doneLabel(card)}
              {card.disposition === "snoozed" && card.snoozedUntil
                ? ` until ${formatShortDateTime(card.snoozedUntil)}`
                : ""}
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={() => void restore()}>
            Review again
          </Button>
        </div>
      </FooterShell>
    );
  }
  const work = card.work;
  const decision = card.status === "blocked" || (work ? workNeedsDecision(work) : false);
  const researching =
    work?.kind === "research" && (work.status === "queued" || work.status === "working");
  const label = researching
    ? work.status === "working"
      ? "Claude is looking this up"
      : "Research is next for Claude"
    : work
      ? workPhaseLabel(work)
      : card.status === "queued"
        ? "Queued"
        : "Working";
  const queuedOperator = work?.lane === "operator" && work.status === "queued";
  return (
    <FooterShell tone={decision ? "note" : "plain"}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={cn("mm-kicker", decision && "!text-note-ink")}>
            {decision ? "Needs a decision" : card.status === "queued" ? "Queued" : "Working"}
          </p>
          <p className={cn("font-medium", decision ? "text-note-ink" : "text-ink")}>{label}</p>
          {work?.notBefore && work.status === "queued" && work.lane === "executor" ? (
            <QueuedRunNote card={card} work={work} />
          ) : null}
          {work?.instruction && work.lane === "operator" && !queuedOperator ? (
            <p className="mt-0.5 text-sm text-muted">“{work.instruction}”</p>
          ) : null}
        </div>
        {queuedOperator || card.status === "queued" ? (
          <Button size="sm" variant="ghost" onClick={() => void restore()}>
            Move back to review
          </Button>
        ) : null}
      </div>
      {queuedOperator && work ? (
        <QueuedNoteEditor key={`${work.id}:${work.instruction}`} work={work} />
      ) : null}
      {work ? (
        <WorkControls
          work={{
            ...work,
            // A scheduled send has its own Change and Cancel (QueuedRunNote).
            recovery: work.recovery.filter(
              (r) => r !== "edit" && !(r === "cancel" && work.scheduledFor),
            ),
          }}
        />
      ) : null}
    </FooterShell>
  );
}

export interface DecisionCardProps {
  summary: CardSummary;
  card: CardView | undefined;
  controller: CardController;
  defaultCleanupKind: ActionKind | null;
  showAccount: boolean;
  colorIndex: number;
  accountLabel: string | undefined;
  active: boolean;
  onOpenThread: () => void;
  loadError?: boolean;
  onRetry?: () => void;
}

export function DecisionCard({
  summary,
  card,
  controller,
  defaultCleanupKind,
  showAccount,
  colorIndex,
  accountLabel,
  active,
  onOpenThread,
  loadError,
  onRetry,
}: DecisionCardProps) {
  const now = useNow(60_000);
  const view = card ?? summary;
  const emailLinks = useMemo(() => emailLinkSet(card?.mailCheck), [card?.mailCheck]);
  return (
    <article
      aria-label={view.title}
      data-card-id={view.id}
      className={cn(
        "relative flex flex-col rounded-xl border bg-paper",
        active ? "border-line-strong shadow-soft" : "border-line",
      )}
    >
      {active ? (
        <span aria-hidden className="absolute inset-y-3 left-0 w-[3px] rounded-r bg-accent" />
      ) : null}
      <div className="flex flex-col gap-3 px-5 pt-5 pb-5 md:px-7 md:pt-6">
        <header className="flex items-start gap-3">
          <span
            aria-hidden
            className={cn(
              "mt-1.5 size-2.5 shrink-0 rounded-full",
              view.kind !== "feed_improvement" && "bg-faint",
            )}
            style={view.kind === "feed_improvement" ? { background: "var(--acct-2)" } : undefined}
          />
          <div className="min-w-0 flex-1">
            <CardMeta
              card={view}
              showAccount={showAccount}
              colorIndex={colorIndex}
              accountLabel={accountLabel}
              now={now}
            />
            <h2 className="mt-1 text-[22px] leading-tight break-words md:text-[26px]">
              {view.title}
            </h2>
            <CardStartLines summary={summary} card={card} actionable={controller.actionable} />
          </div>
        </header>
        {view.threadKey ? (
          <nav
            aria-label="Original email"
            className="flex flex-wrap items-center gap-2 md:pl-[22px]"
          >
            <Button size="sm" variant="secondary" onClick={onOpenThread}>
              <BookOpen className="size-4" aria-hidden />
              Read email
            </Button>
            <SaveMailButton
              threadKey={view.threadKey}
              command={{ id: "card.save", enabled: active }}
            />
            <AddTaskButton threadKey={view.threadKey} title={view.title} card={card} />
            {card ? (
              <ReplyThoughts
                card={card}
                disabled={controller.busy || card.status === "queued" || card.status === "working"}
                beforeQueue={async () => {
                  controller.disarm();
                  return controller.flushSaves();
                }}
              />
            ) : null}
            <AddToCalendarButton
              threadKey={view.threadKey}
              command={{ id: "card.addEvent", enabled: active }}
            />
            {/* The one Open in Gmail control on a card (the exact message by Message-ID). */}
            <OpenInGmail
              accountEmail={view.accountEmail}
              threadKey={view.threadKey}
              rfc822MessageId={card?.rfc822MessageId}
              variant="button"
            />
          </nav>
        ) : null}
        <EmailLinksContext.Provider value={emailLinks}>
          <div className="flex flex-col gap-3 md:pl-[22px]">
            {/* U6: your note on the thread comes first; pin and deadline reminder with it. */}
            {card ? (
              <CardMemory cardId={card.id} threadKey={card.threadKey} active={active} />
            ) : null}
            <FormattedText text={view.why} className="text-ink-2" />
            {card ? <MailCheckNotes card={card} /> : null}
            {card ? <ScheduledReturnNote card={card} /> : null}
            {view.breakthrough && isToReview(view) ? (
              <p className="text-sm text-note-ink">
                Needs you now{view.breakthroughRule ? ` · ${view.breakthroughRule}` : ""}
              </p>
            ) : null}
            {card ? (
              <div className="mt-1 flex flex-col gap-5">
                {/* U4: a card that needs a reply says where its draft is. */}
                <DraftNotice card={card} actionable={controller.actionable} />
                {blocksBesideWhy(card.why, card.blocks)
                  .filter((block) => block.type !== "email_thread" || !view.threadKey)
                  .map((block) => (
                    <BlockView
                      key={block.id}
                      block={block}
                      cardId={card.id}
                      onOpenThread={onOpenThread}
                      slotsContext={
                        block.type === "slots"
                          ? {
                              cardId: card.id,
                              feedId: card.feedId,
                              accountId: card.accountId,
                              updatedAt: card.updatedAt,
                              actionable: controller.actionable && !card.updatePending,
                            }
                          : undefined
                      }
                      editable={
                        block.type === "editable_text"
                          ? {
                              cardId: card.id,
                              value: controller.values[block.id] ?? block.value ?? "",
                              variantIndex: controller.variantIndex[block.id] ?? 0,
                              onChange: (v) => controller.setValue(block.id, v),
                              onBlur: () =>
                                void controller.saveBlock(block.id).catch(() => undefined),
                              onVariant: (i) => controller.chooseVariant(block.id, i),
                              readOnly: !controller.actionable || controller.busy,
                            }
                          : undefined
                      }
                    />
                  ))}
                <Receipts receipts={card.receipts} />
                <CardSenderLinks card={card} />
              </div>
            ) : loadError ? (
              <div className="rounded-lg border border-line bg-bg px-4 py-3 text-sm text-muted">
                Couldn’t load this card’s details.{" "}
                <button type="button" className="text-accent-text underline" onClick={onRetry}>
                  Try again
                </button>
              </div>
            ) : (
              <div aria-hidden className="mt-1 flex flex-col gap-2">
                <div className="h-3 w-full rounded bg-sunk" />
                <div className="h-3 w-11/12 rounded bg-sunk" />
                <div className="h-20 w-full rounded-lg bg-sunk" />
              </div>
            )}
          </div>
        </EmailLinksContext.Provider>
      </div>
      {card ? <CardHistory entries={card.history} now={now} /> : null}
      {card ? (
        isToReview(card) ? (
          <ReviewFooter
            card={card}
            controller={controller}
            defaultCleanupKind={defaultCleanupKind}
          />
        ) : (
          <StateFooter card={card} />
        )
      ) : null}
    </article>
  );
}
